import { useEffect, useState, type CSSProperties } from 'react';
import Popover from '@mui/material/Popover';
import type { AppSnapshot } from '../../app/state';
import { DocResponsiblesPicker } from '../../components/DocResponsiblesPicker';
import { asWorkflowState } from '../../lib/docs';
import { canAiAdmin } from '../../lib/permissions';
import { canAssignDoc } from '../../lib/responsibility';
import type { DocRowDoc } from '../../lib/docTable';
import { fmtDate, stateColorOf, stateLabelOf } from './documentsCells';

/**
 * ONE LANGUAGE of ONE PAGE — the panel behind every language chip and every Responsible cell.
 *
 * It answers the question the table's row can only summarise: where does the FR version stand,
 * how far behind the source is it, who owns it, and what can I do about it from here. The two
 * entry points differ only in what they scroll to; the panel is the same, because "the FR chip"
 * and "who owns FR" are one fact seen from two columns.
 *
 * NO WORKFLOW VERBS, deliberately. Approve / request review / send back live in the document's
 * own bar, where the text being judged is on screen. A dashboard that could approve a page
 * nobody had opened would be a way to clear a backlog without reading it.
 *
 * ASSIGNING A LANGUAGE THAT HAS NO PAGE is allowed and is the point (V0.42's rule, inherited
 * from the doc bar): it is how "please write the French version" is said. The picker is the
 * shared `DocResponsiblesPicker`, keyed on `${docId}:${locale}` so a different language never
 * inherits the previous one's draft.
 */

export function LocalePopover({
  s,
  row,
  locale,
  anchor,
  sourceLocale,
  focusResponsibles,
  canTranslateTo,
  onClose,
  onOpenDoc,
  onTranslate,
}: {
  s: AppSnapshot;
  row: DocRowDoc;
  locale: string;
  anchor: HTMLElement | null;
  sourceLocale: string;
  focusResponsibles: boolean;
  canTranslateTo: (row: DocRowDoc, code: string) => boolean;
  onClose: () => void;
  onOpenDoc: (row: DocRowDoc, code: string) => void;
  onTranslate: (row: DocRowDoc, code: string) => void;
}) {
  const cell = row.byLocale[locale] ?? null;
  const written = !!cell?.written;
  const lang = s.languages.find((l) => l.code === locale);
  const langName = lang?.name ?? locale.toUpperCase();
  const code = locale.toUpperCase();
  const src = sourceLocale.toUpperCase();
  const aiVisible = row.ai !== undefined && canAiAdmin(s.perm);

  // The optimistic echo of the picker: what this panel shows the instant a chip moves. The
  // TABLE catches up on its own — `setDocResponsibles` replaces `s.docWorkflow`, which is what
  // the tab's fetch is keyed on.
  const [users, setUsers] = useState<readonly string[]>(cell?.responsibles ?? []);
  const [groups, setGroups] = useState<readonly string[]>(cell?.responsibleGroups ?? []);
  useEffect(() => {
    setUsers(cell?.responsibles ?? []);
    setGroups(cell?.responsibleGroups ?? []);
  }, [row.docPageId, locale, cell]);

  const mayAssign = canAssignDoc(s, row.docPageId);
  const directoriesReady = s.userDirectoryLoaded && s.groupDirectoryLoaded;
  const nameOf = (id: string) => s.userDirectory.find((u) => u.id === id)?.displayName ?? id;
  const groupNameOf = (id: string) => s.groupDirectory.find((g) => g.id === id)?.name ?? id;

  const version = cell?.ai?.version ?? null;
  const versionText = () => {
    if (locale === sourceLocale) {
      return aiVisible && version !== null ? `V${version} · source language` : 'source language';
    }
    const prefix = aiVisible && version !== null ? `V${version} · ` : '';
    if (cell?.stale) {
      return (
        <>
          {prefix}
          <span style={{ color: 'var(--review-in-review)' }}>
            {cell.behind} behind {src}
          </span>
        </>
      );
    }
    return `${prefix}up to date with ${src}`;
  };

  const showTranslate = locale !== sourceLocale && canTranslateTo(row, locale);

  return (
    <Popover
      open={!!anchor}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <div id="dt-pop" className="dt-pop">
        <div className="review-head">
          <span className="review-title">{row.title || '(untitled)'}</span>
          <span className="review-lang">{langName}</span>
        </div>

        <div className="dt-pop-row">
          <span className="k">State</span>
          <span className="v">
            {written ? (
              <span className="dt-state">
                <span
                  className="dt-dot"
                  style={{ '--c': stateColorOf(asWorkflowState(cell!.workflowState)) } as CSSProperties}
                />
                {stateLabelOf(asWorkflowState(cell!.workflowState))}
              </span>
            ) : (
              <span className="dt-state muted">
                <span className="dt-dot dash" />
                Not written yet in {langName}
              </span>
            )}
          </span>
        </div>

        {written ? (
          <>
            <div className="dt-pop-row">
              <span className="k">Reviewed</span>
              <span className="v">
                {cell!.reviewedByName ? (
                  `${cell!.reviewedByName} · ${fmtDate(cell!.reviewedAt)}`
                ) : (
                  <span className="tgt-meta">not by hand</span>
                )}
              </span>
            </div>
            <div className="dt-pop-row">
              <span className="k">Version</span>
              <span className="v">{versionText()}</span>
            </div>
            <div className="dt-pop-row">
              <span className="k">Last change</span>
              <span className="v">
                {fmtDate(cell!.lastChangeAt)}
                {cell!.lastChangeBy ? ` · ${cell!.lastChangeBy}` : ''}
              </span>
            </div>
            {aiVisible ? (
              <div className="dt-pop-row">
                <span className="k">Chatbot index</span>
                <span className="v">
                  {cell!.ai?.embedded ? (
                    <span style={{ color: 'var(--review-approved)' }}>indexed</span>
                  ) : (
                    <span className="tgt-meta">not indexed</span>
                  )}
                </span>
              </div>
            ) : null}
          </>
        ) : null}

        <div className="dt-pop-sec">Responsible for the {code} version</div>
        {mayAssign && directoriesReady ? (
          <DocResponsiblesPicker
            key={`${row.docPageId}:${locale}`}
            id="dt-responsibles"
            docId={row.docPageId}
            locale={locale}
            s={s}
            seedUsers={users}
            seedGroups={groups}
            autoOpen={focusResponsibles}
            onChange={(nextUsers, nextGroups) => {
              setUsers(nextUsers);
              setGroups(nextGroups);
            }}
          />
        ) : (
          <div className="review-empty" id="dt-responsibles-read">
            {users.length || groups.length
              ? [...users.map(nameOf), ...groups.map(groupNameOf)].join(', ')
              : 'Nobody yet.'}
          </div>
        )}

        {!mayAssign ? (
          <div className="dt-pop-note">
            Only a branch responsible or a documentation administrator can change who is assigned
            here.
          </div>
        ) : null}
        {!written ? (
          <div className="dt-pop-note">
            Assigning someone to a language with no page yet is how “please write the {langName}{' '}
            version” is said.
          </div>
        ) : null}

        <div className="dt-pop-acts">
          <button type="button" className="aidoc-actbtn dt-pop-open" onClick={() => onOpenDoc(row, locale)}>
            Open in {code}
          </button>
          {showTranslate ? (
            <button
              type="button"
              className="aidoc-actbtn regen dt-pop-translate"
              onClick={() => onTranslate(row, locale)}
            >
              {written ? 'Re-translate' : 'Translate'} into {code}
            </button>
          ) : null}
        </div>
      </div>
    </Popover>
  );
}
